/**
 * Ambient weather for Timeline day headers.
 *
 * Uses Open-Meteo (free, no API key, no attribution required):
 *   - Geocoding:  https://geocoding-api.open-meteo.com/v1/search?name=…
 *   - Forecast:   https://api.open-meteo.com/v1/forecast?…daily=…
 *
 * Cached in localStorage keyed by trip id + destination string so we don't
 * hit the network on every mount. TTL ~6h — good enough for planning
 * without going stale during a live trip.
 *
 * Everything is best-effort — geocoding fails, network fails, cache
 * absent → we simply return an empty map and the Timeline day headers
 * render without weather pills.
 */

interface GeocodeResult {
  latitude: number;
  longitude: number;
  name: string;
  country?: string;
}

/** Open-Meteo WMO weather codes mapped to our icon families. */
export type WeatherKind =
  | "clear"
  | "cloudy"
  | "fog"
  | "drizzle"
  | "rain"
  | "snow"
  | "storm";

export interface DailyWeather {
  /** Local yyyy-MM-dd */
  date: string;
  kind: WeatherKind;
  /** °C */
  tempMax: number;
  /** °C */
  tempMin: number;
}

/** Map keyed by yyyy-MM-dd local date string. */
export type WeatherByDate = Record<string, DailyWeather>;

const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h
const CACHE_PREFIX = "travelix:weather:";

interface CachedForecast {
  savedAt: number;
  data: WeatherByDate;
  destination: string;
}

function cacheKey(tripId: string) {
  return `${CACHE_PREFIX}${tripId}`;
}

function readCache(tripId: string, destination: string): WeatherByDate | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(tripId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedForecast;
    if (parsed.destination !== destination) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(tripId: string, destination: string, data: WeatherByDate) {
  try {
    const payload: CachedForecast = {
      savedAt: Date.now(),
      data,
      destination,
    };
    window.localStorage.setItem(cacheKey(tripId), JSON.stringify(payload));
  } catch {
    /* storage disabled — non-fatal */
  }
}

async function geocode(name: string): Promise<GeocodeResult | null> {
  const url = new URL("https://geocoding-api.open-meteo.com/v1/search");
  url.searchParams.set("name", name);
  url.searchParams.set("count", "1");
  url.searchParams.set("format", "json");
  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const json = (await res.json()) as { results?: GeocodeResult[] };
  return json.results?.[0] ?? null;
}

function kindFromWmoCode(code: number): WeatherKind {
  // https://open-meteo.com/en/docs — WMO Weather Interpretation Codes
  if ([0, 1].includes(code)) return "clear";
  if ([2, 3].includes(code)) return "cloudy";
  if ([45, 48].includes(code)) return "fog";
  if ([51, 53, 55, 56, 57].includes(code)) return "drizzle";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "rain";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "snow";
  if ([95, 96, 99].includes(code)) return "storm";
  return "cloudy";
}

async function forecast(
  lat: number,
  lon: number,
  startDate: string,
  endDate: string,
): Promise<WeatherByDate> {
  const url = new URL("https://api.open-meteo.com/v1/forecast");
  url.searchParams.set("latitude", String(lat));
  url.searchParams.set("longitude", String(lon));
  url.searchParams.set(
    "daily",
    "weathercode,temperature_2m_max,temperature_2m_min",
  );
  url.searchParams.set("timezone", "auto");
  url.searchParams.set("start_date", startDate);
  url.searchParams.set("end_date", endDate);
  const res = await fetch(url.toString());
  if (!res.ok) return {};
  const json = (await res.json()) as {
    daily?: {
      time: string[];
      weathercode: number[];
      temperature_2m_max: number[];
      temperature_2m_min: number[];
    };
  };
  const d = json.daily;
  if (!d) return {};
  const out: WeatherByDate = {};
  for (let i = 0; i < d.time.length; i++) {
    const date = d.time[i]!;
    out[date] = {
      date,
      kind: kindFromWmoCode(d.weathercode[i] ?? 0),
      tempMax: Math.round(d.temperature_2m_max[i] ?? 0),
      tempMin: Math.round(d.temperature_2m_min[i] ?? 0),
    };
  }
  return out;
}

/**
 * Fetch a forecast for a trip. Returns an empty map on any failure.
 * The API caps at ~16 days ahead; days outside that range simply won't
 * appear in the result.
 */
export async function fetchTripWeather(args: {
  tripId: string;
  destination: string;
  /** yyyy-MM-dd */
  startDate: string;
  /** yyyy-MM-dd */
  endDate: string;
}): Promise<WeatherByDate> {
  const { tripId, destination, startDate, endDate } = args;
  if (!destination) return {};

  const cached = readCache(tripId, destination);
  if (cached) return cached;

  try {
    const geo = await geocode(destination);
    if (!geo) return {};
    const data = await forecast(geo.latitude, geo.longitude, startDate, endDate);
    writeCache(tripId, destination, data);
    return data;
  } catch {
    return {};
  }
}
